import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { CATALOGS, I18nContext, loadLang, saveLang, type Lang } from '@/i18n'
import { cn } from '@/lib/cn'

/* ═══ Til ═══════════════════════════════════════════════════════ */

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => loadLang())

  const setLang = useCallback((l: Lang) => {
    saveLang(l)
    setLangState(l)
  }, [])

  const value = useMemo(() => ({ lang, t: CATALOGS[lang], setLang }), [lang, setLang])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

/* ═══ Mavzu ═════════════════════════════════════════════════════
   SKILL §2: dark DEFAULT (moderatorlar uzoq ishlaydi). */

export type Theme = 'dark' | 'light'
const THEME_KEY = 'grom_admin_theme'

interface ThemeValue {
  theme: Theme
  setTheme: (t: Theme) => void
}
const ThemeContext = createContext<ThemeValue>({ theme: 'dark', setTheme: () => {} })

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(
    () => (document.documentElement.dataset.theme as Theme) || 'dark',
  )

  const setTheme = useCallback((t: Theme) => {
    document.documentElement.dataset.theme = t
    try {
      localStorage.setItem(THEME_KEY, t)
    } catch {
      /* localStorage yopiq */
    }
    setThemeState(t)
  }, [])

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeValue {
  return useContext(ThemeContext)
}

/* ═══ Toast ═════════════════════════════════════════════════════
   SKILL §13:
     toast          — 4 s (xato 8 s)
     toast + undo   — 10 s
   Toast HECH QACHON kontentni yopmaydi — o'ng pastda, jadval ustidan emas. */

export type ToastTone = 'success' | 'error' | 'info' | 'warning'

export interface ToastOptions {
  tone?: ToastTone
  description?: string
  /** Berilsa — «Bekor qilish» tugmasi chiqadi va davomiylik 10 s bo'ladi. */
  undo?: () => void | Promise<void>
  durationMs?: number
}

interface ToastRecord extends ToastOptions {
  id: number
  message: string
}

interface ToastValue {
  toast: (message: string, opts?: ToastOptions) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastValue>({ toast: () => {}, dismiss: () => {} })

const TONE_STYLES: Record<ToastTone, string> = {
  success: 'border-l-success',
  error: 'border-l-danger',
  warning: 'border-l-warning',
  info: 'border-l-info',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastRecord[]>([])
  const seq = useRef(0)
  const timers = useRef(new Map<number, number>())

  const dismiss = useCallback((id: number) => {
    const h = timers.current.get(id)
    if (h !== undefined) {
      window.clearTimeout(h)
      timers.current.delete(id)
    }
    setItems((prev) => prev.filter((i) => i.id !== id))
  }, [])

  const toast = useCallback(
    (message: string, opts: ToastOptions = {}) => {
      const id = ++seq.current
      const tone = opts.tone ?? 'info'
      const duration = opts.durationMs ?? (opts.undo ? 10_000 : tone === 'error' ? 8_000 : 4_000)
      setItems((prev) => [...prev, { ...opts, id, message, tone }])
      timers.current.set(
        id,
        window.setTimeout(() => dismiss(id), duration),
      )
    },
    [dismiss],
  )

  useEffect(() => {
    const map = timers.current
    return () => {
      for (const h of map.values()) window.clearTimeout(h)
      map.clear()
    }
  }, [])

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2"
          role="region"
          aria-live="polite"
        >
          {items.map((item) => (
            <div
              key={item.id}
              className={cn(
                'pointer-events-auto rounded-md border border-border-default border-l-2 bg-surface-2 px-3 py-2.5',
                TONE_STYLES[item.tone ?? 'info'],
              )}
              style={{ boxShadow: 'var(--shadow-popover)' }}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-base text-primary">{item.message}</p>
                  {item.description && <p className="mt-0.5 text-sm text-secondary">{item.description}</p>}
                </div>
                {item.undo && (
                  <button
                    type="button"
                    className="shrink-0 text-sm font-medium text-accent hover:underline"
                    onClick={async () => {
                      dismiss(item.id)
                      await item.undo?.()
                    }}
                  >
                    ↩
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastValue {
  return useContext(ToastContext)
}
